/**
 * DietaryStep - Fun quiz-style dietary preferences and health information
 */

import React from "react";
import {
  Box,
  Typography,
  Stack,
} from "@mui/material";
import type { DogFormData } from "../../types";
import { Allergy, HealthIssue } from "@/helpers/enums";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  form: Partial<DogFormData>;
  onChange: (updates: Partial<DogFormData>) => void;
}

const ALLERGIES = Object.values(Allergy);
const HEALTH_ISSUES = Object.values(HealthIssue);

export function DietaryStep({ form, onChange }: Props) {
  const allergies = form.allergies || [];
  const healthIssues = form.healthIssues || [];

  const toggleAllergy = (allergy: string) => {
    const newAllergies = allergies.includes(allergy)
      ? allergies.filter((a) => a !== allergy)
      : [...allergies, allergy];
    onChange({ allergies: newAllergies });
  };

  const toggleHealthIssue = (issue: string) => {
    const newIssues = healthIssues.includes(issue)
      ? healthIssues.filter((h) => h !== issue)
      : [...healthIssues, issue];
    onChange({ healthIssues: newIssues });
  };

  return (
    <Stack spacing={3}>
      {/* Allergies */}
      <Box
        sx={{
          bgcolor: QUIZ.color.warmWhite,
          borderRadius: "16px",
          p: 3,
          border: "2px solid #D7C7B4",
          maxWidth: { xs: "100%", md: 500 },
          mx: "auto",
          width: "100%",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown, mb: 2 }}>
          Any food allergies? (select all that apply)
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {ALLERGIES.map((allergy) => {
            const isSelected = allergies.includes(allergy);
            return (
              <Box
                key={allergy}
                onClick={() => toggleAllergy(allergy)}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "20px",
                  bgcolor: isSelected ? "#9AE600" : QUIZ.color.white,
                  border: `2px solid ${isSelected ? QUIZ.color.brown : QUIZ.color.cream3}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: QUIZ.color.brown,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? QUIZ.color.brown : QUIZ.color.brown,
                  }}
                >
                  {allergy}
                </Typography>
              </Box>
            );
          })}
        </Box>
        {allergies.length === 0 && (
          <Typography
            sx={{
              fontSize: 12,
              color: QUIZ.color.brown,
              opacity: 0.6,
              mt: 1.5,
              fontStyle: "italic",
            }}
          >
            No allergies? That's great! 🎉
          </Typography>
        )}
      </Box>

      {/* Health Issues */}
      <Box
        sx={{
          bgcolor: QUIZ.color.warmWhite,
          borderRadius: "16px",
          p: 3,
          border: "2px solid #D7C7B4",
          maxWidth: { xs: "100%", md: 500 },
          mx: "auto",
          width: "100%",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown, mb: 2 }}>
          Any health concerns? (select all that apply)
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {HEALTH_ISSUES.map((issue) => {
            const isSelected = healthIssues.includes(issue);
            return (
              <Box
                key={issue}
                onClick={() => toggleHealthIssue(issue)}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: "20px",
                  bgcolor: isSelected ? "#E6E0FF" : QUIZ.color.white,
                  border: `2px solid ${isSelected ? QUIZ.color.brown : QUIZ.color.cream3}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: QUIZ.color.brown,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? QUIZ.color.brown : QUIZ.color.brown,
                  }}
                >
                  {issue}
                </Typography>
              </Box>
            );
          })}
        </Box>
        {healthIssues.length === 0 && (
          <Typography
            sx={{
              fontSize: 12,
              color: QUIZ.color.brown,
              opacity: 0.6,
              mt: 1.5,
              fontStyle: "italic",
            }}
          >
            A healthy pup! 💪
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
